import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAllUsers, type AllUserRow } from '@/features/users/api';
import { useBulkAddGroupUsers, useGroupsList } from '../api';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';

export function BulkAddGroupPage() {
  const canBulkAdd = usePermission('Manage Groups.Bulk Group Add', BASE_PERMISSION.ADD);
  const groups = useGroupsList();
  const users = useAllUsers();
  const bulkAdd = useBulkAddGroupUsers();

  if (!canBulkAdd) return <Navigate to="/group-list" replace />;

  const [groupId, setGroupId] = useState<string>('');
  const [pending, setPending] = useState<{ ids: string[]; clear: () => void } | null>(null);

  const columns = useMemo<ColumnDef<AllUserRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId}
          </span>
        ),
      },
      { accessorKey: 'username', header: 'Username' },
    ],
    [],
  );

  const groupLabel =
    (groups.data ?? []).find((g) => String(g.groupId) === groupId)?.groupName ?? '';

  const performBulkAdd = () => {
    if (!pending) return;
    bulkAdd.mutate(
      { groupId, userIds: pending.ids.join(',') },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ?? `Added ${pending.ids.length} users to ${groupLabel}`,
          );
          pending.clear();
          setPending(null);
        },
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Bulk add failed.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Bulk Add Users to Group"
        description="Attach many users to a group in one operation."
        actions={
          <Button asChild variant="outline">
            <Link to="/group-list">
              <ArrowLeft className="h-4 w-4" />
              Back to groups
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Target group</CardTitle>
          <CardDescription>
            Users you select below will be added to this group.
          </CardDescription>
        </CardHeader>
        <CardContent className="max-w-md">
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger>
              <SelectValue
                placeholder={groups.isLoading ? 'Loading groups…' : 'Select a group'}
              />
            </SelectTrigger>
            <SelectContent>
              {(groups.data ?? []).map((g) => (
                <SelectItem key={g.groupId} value={String(g.groupId)}>
                  {g.groupName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <DataTable<AllUserRow, unknown>
        tableId="bulk:add-group:v1"
        columns={columns}
        data={users.data ?? []}
        isLoading={users.isLoading}
        searchPlaceholder="Search users…"
        emptyTitle="No users available"
        emptyDescription="There are no users to assign."
        getRowId={(row) => String(row.userId)}
        enableRowSelection
        exportFilename="users.csv"
        pageSize={25}
        renderSelectionActions={(ids, clear) => (
          <Button
            size="sm"
            onClick={() => {
              if (!groupId) {
                toast.error('Pick a target group first.');
                return;
              }
              setPending({ ids, clear });
            }}
            disabled={!groupId || ids.length === 0 || bulkAdd.isPending}
          >
            <UserPlus className="h-4 w-4" />
            Add {ids.length} to {groupLabel || 'group'}
          </Button>
        )}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title="Are you sure?"
        description={
          <>
            Add <span className="font-medium text-foreground">{pending?.ids.length ?? 0}</span> user
            {pending?.ids.length === 1 ? '' : 's'} to{' '}
            <span className="font-medium text-foreground">{groupLabel}</span>?
          </>
        }
        confirmLabel="Add users"
        loading={bulkAdd.isPending}
        onConfirm={performBulkAdd}
      />
    </FillPage>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
